import React, { useMemo, useState } from 'react';
import './firebaseSidepanel.css';
import { useApiData } from '../../../../contexts/apiService';
import { submitFirebaseCellEdit } from '../../table_cell/firebaseEdit';
import Dropdown from '../../../common/dropdown/dropdown';
import AddButton from '../../../common/button_add/addButton';
import DeleteButton from '../../../common/button_delete/deleteButton';

interface FirebaseSidePanelProps {
    isOpen: boolean;
    rowData: any;
    onClose: () => void;
}

const isTruthyAccessValue = (value: any): boolean => {
    if (value === true || value === 1 || value === '1' || value === 'true') return true;
    if (value && typeof value === 'object' && value.enabled === true) return true;
    return false;
};

const normalizeAccessIds = (source: any): string[] => {
    if (!source) return [];

    if (Array.isArray(source)) {
        return Array.from(new Set(source.map(v => String(v)).filter(Boolean)));
    }

    if (typeof source === 'object') {
        return Object.keys(source).filter(key => isTruthyAccessValue(source[key]));
    }

    return [];
};

const toAccessMap = (source: any): Record<string, boolean> => {
    const ids = normalizeAccessIds(source);
    return ids.reduce((acc, id) => {
        acc[id] = true;
        return acc;
    }, {} as Record<string, boolean>);
};

const FirebaseSidePanel: React.FC<FirebaseSidePanelProps> = ({ isOpen, rowData }) => {
    const { apiData, fetchAllUsers } = useApiData();

    const [selectedEntityId, setSelectedEntityId] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const userUid = String(rowData?.uid || '');
    const producerId = rowData?.producer_id != null ? String(rowData.producer_id) : '';

    const producerById = useMemo(() => {
        const map = new Map<string, any>();
        (apiData?.producer_definition || []).forEach((producer: any) => {
            map.set(String(producer.id), producer);
        });
        return map;
    }, [apiData?.producer_definition]);

    const producerName = useMemo(() => {
        if (!producerId) return '';
        const producer = producerById.get(String(producerId));
        return producer?.producer_name || '';
    }, [producerById, producerId]);

    const dataAccess = rowData?.dataAccess || {};
    const entityIds = normalizeAccessIds(dataAccess.entity_id);
    const groupIds = normalizeAccessIds(dataAccess.group_id);

    const entityById = useMemo(() => {
        const map = new Map<string, any>();
        (apiData?.entity_definition || []).forEach((entity: any) => {
            map.set(String(entity.id), entity);
        });
        return map;
    }, [apiData?.entity_definition]);

    const groupById = useMemo(() => {
        const map = new Map<string, any>();
        (apiData?.producer_groups || []).forEach((group: any) => {
            map.set(String(group.id), group);
        });
        return map;
    }, [apiData?.producer_groups]);

    const availableEntityOptions = useMemo(() => {
        const history = apiData?.entity_producer_history || [];
        const assigned = new Set(entityIds);

        // producer-scoped entity IDs from entity_producer_history
        const allowedEntityIds = new Set<string>();
        history.forEach((row: any) => {
            const historyProducerId = row?.producer_id ?? row?.current_producer_id;
            const historyEntityId = row?.entity_id ?? row?.id;
            if (
                historyEntityId != null &&
                historyProducerId != null &&
                String(historyProducerId) === producerId
            ) {
                allowedEntityIds.add(String(historyEntityId));
            }
        });

        const options: Array<{ value: string; label: string }> = [];
        allowedEntityIds.forEach((id) => {
            if (assigned.has(id)) return;
            const entity = entityById.get(id);
            options.push({
                value: id,
                label: entity?.entity_name || `Entity ${id}`,
            });
        });

        options.sort((a, b) => a.label.localeCompare(b.label));
        return options;
    }, [apiData?.entity_producer_history, entityById, entityIds, producerId]);

    const availableGroupOptions = useMemo(() => {
        const assigned = new Set(groupIds);
        const groups = apiData?.producer_groups || [];

        const filtered = groups.filter((group: any) => {
            const groupProducerId = group?.producer_id;
            if (!producerId || groupProducerId == null) return true;
            return String(groupProducerId) === producerId;
        });

        const options = filtered
            .filter((group: any) => !assigned.has(String(group.id)))
            .map((group: any) => ({
                value: String(group.id),
                label: group.display_name || group.group_name || group.name || `Group ${group.id}`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        return options;
    }, [apiData?.producer_groups, groupIds, producerId]);

    const saveDataAccess = async (nextDataAccess: any) => {
        if (!userUid) return;

        setIsSaving(true);
        const [success, error] = await submitFirebaseCellEdit({
            collectionName: 'users',
            docId: userUid,
            fieldName: 'dataAccess',
            oldValue: dataAccess,
            newValue: nextDataAccess,
            colType: 'text',
        });

        if (!success) {
            window.alert(`Failed to update data access${error ? `: ${error}` : ''}`);
            setIsSaving(false);
            return;
        }

        await fetchAllUsers();
        setIsSaving(false);
    };

    const handleAddEntity = async () => {
        if (!selectedEntityId) return;
        const nextEntityMap = {
            ...toAccessMap(dataAccess.entity_id),
            [selectedEntityId]: true,
        };
        const nextDataAccess = {
            ...dataAccess,
            entity_id: nextEntityMap,
            group_id: toAccessMap(dataAccess.group_id),
        };
        await saveDataAccess(nextDataAccess);
        setSelectedEntityId('');
    };

    const handleRemoveEntity = async (entityId: string) => {
        const nextEntityMap = { ...toAccessMap(dataAccess.entity_id) };
        delete nextEntityMap[entityId];
        const nextDataAccess = {
            ...dataAccess,
            entity_id: nextEntityMap,
            group_id: toAccessMap(dataAccess.group_id),
        };
        await saveDataAccess(nextDataAccess);
    };

    const handleAddGroup = async () => {
        if (!selectedGroupId) return;
        const nextGroupMap = {
            ...toAccessMap(dataAccess.group_id),
            [selectedGroupId]: true,
        };
        const nextDataAccess = {
            ...dataAccess,
            entity_id: toAccessMap(dataAccess.entity_id),
            group_id: nextGroupMap,
        };
        await saveDataAccess(nextDataAccess);
        setSelectedGroupId('');
    };

    const handleRemoveGroup = async (groupId: string) => {
        const nextGroupMap = { ...toAccessMap(dataAccess.group_id) };
        delete nextGroupMap[groupId];
        const nextDataAccess = {
            ...dataAccess,
            entity_id: toAccessMap(dataAccess.entity_id),
            group_id: nextGroupMap,
        };
        await saveDataAccess(nextDataAccess);
    };

    if (!isOpen) return null;

    return (
        <div className="firebaseSidepanel-container">
            <div className="firebaseSidepanel-titleRow">
                <h3 className="firebaseSidepanel-title">Data Access</h3>
                <div className="firebaseSidepanel-subtitle">
                    {rowData?.name || rowData?.email || 'User'}
                </div>
                <div className="firebaseSidepanel-subtitle">
                    {producerName || 'N/A'}
                </div>
            </div>

            <div className="firebaseSidepanel-section">
                <div className="firebaseSidepanel-sectionHeader">Entities</div>

                <div className="firebaseSidepanel-controls">
                    <Dropdown
                        options={availableEntityOptions}
                        value={selectedEntityId}
                        onChange={setSelectedEntityId}
                        placeholder="Select entity..."
                        isDisabled={isSaving || availableEntityOptions.length === 0}
                        width={260}
                        height={32}
                        className="dropdown-inputMatch"
                    />
                    <AddButton
                        onClick={handleAddEntity}
                        disabled={!selectedEntityId || isSaving}
                    />
                </div>

                {entityIds.length === 0 ? (
                    <div className="firebaseSidepanel-empty">No entity access assigned</div>
                ) : (
                    <div className="firebaseSidepanel-list">
                        {entityIds.map((id) => {
                            const entity = entityById.get(String(id));
                            const name = entity?.entity_name || 'Unknown entity';
                            return (
                                <div key={'entity-' + id} className="firebaseSidepanel-listItem">
                                    <span className="firebaseSidepanel-label">{name}</span>
                                    <DeleteButton
                                        onClick={() => handleRemoveEntity(id)}
                                        disabled={isSaving}
                                        text="Remove"
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="firebaseSidepanel-section">
                <div className="firebaseSidepanel-sectionHeader">Groups</div>

                <div className="firebaseSidepanel-controls">
                    <Dropdown
                        options={availableGroupOptions}
                        value={selectedGroupId}
                        onChange={setSelectedGroupId}
                        placeholder="Select group..."
                        isDisabled={isSaving || availableGroupOptions.length === 0}
                        width={260}
                        height={32}
                        className="dropdown-inputMatch"
                    />
                    <AddButton
                        onClick={handleAddGroup}
                        disabled={!selectedGroupId || isSaving}
                    />
                </div>

                {groupIds.length === 0 ? (
                    <div className="firebaseSidepanel-empty">No group access assigned</div>
                ) : (
                    <div className="firebaseSidepanel-list">
                        {groupIds.map((id) => {
                            const group = groupById.get(String(id));
                            const name = group?.display_name || group?.group_name || group?.name || 'Unknown group';
                            return (
                                <div key={'group-' + id} className="firebaseSidepanel-listItem">
                                    <span className="firebaseSidepanel-label">{name}</span>
                                    <DeleteButton
                                        onClick={() => handleRemoveGroup(id)}
                                        disabled={isSaving}
                                        text="Remove"
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FirebaseSidePanel;